<template>
  <UIModal
    class="ModalGptPublicLink"
    data-testid="gpt-public-link-modal"
    size="lg"
    :isOpen="isOpen"
    @close="emit('close')"
  >
    <template #title>{{ title }}</template>

    <template #content>
      <!-- Publish confirmation -->
      <div
        v-if="confirmingPublish"
        class="ModalGptPublicLink__body"
        data-testid="gpt-public-link-confirm-view"
      >
        <p class="ModalGptPublicLink__intro">
          <template v-if="willBecomePublic">
            Publishing makes this GPT public — it becomes available to all
            Foundever users in EverGPT — and creates a shareable web link.
          </template>
          <template v-else>
            This creates a shareable web link for this GPT.
          </template>
        </p>

        <div class="ModalGptPublicLink__gpt-row">
          <div
            class="ModalGptPublicLink__gpt-icon"
            :style="`background: ${gpt.gradient}`"
          />
          <div class="ModalGptPublicLink__gpt-text">
            <span class="ModalGptPublicLink__gpt-name">{{ gpt.name }}</span>
            <span class="ModalGptPublicLink__gpt-meta">
              Owned by {{ ownerName }}
            </span>
          </div>
        </div>

        <p class="ModalGptPublicLink__note">
          Do you really want to publish this GPT?
        </p>
      </div>

      <!-- Unpublish confirmation -->
      <div
        v-else-if="confirmingUnpublish"
        class="ModalGptPublicLink__body"
        data-testid="gpt-public-link-unpublish-view"
      >
        <p class="ModalGptPublicLink__intro">
          This GPT will no longer be accessible through its web link.
        </p>

        <div class="ModalGptPublicLink__gpt-row">
          <div
            class="ModalGptPublicLink__gpt-icon"
            :style="`background: ${gpt.gradient}`"
          />
          <div class="ModalGptPublicLink__gpt-text">
            <span class="ModalGptPublicLink__gpt-name">{{ gpt.name }}</span>
            <span class="ModalGptPublicLink__gpt-meta">
              Owned by {{ ownerName }} · Published to web
            </span>
          </div>
        </div>

        <p class="ModalGptPublicLink__note">
          This won’t affect the GPT or its existing settings in EverGPT.
        </p>
      </div>

      <!-- Loading -->
      <div v-else-if="step === 'loading'" class="ModalGptPublicLink__loading">
        <UISpinner size="md" />
      </div>

      <!-- Publish -->
      <div
        v-else-if="step === 'publish'"
        class="ModalGptPublicLink__body"
        data-testid="gpt-public-link-publish-view"
      >
        <p class="ModalGptPublicLink__intro">
          Publishing creates a shareable web link that allows Foundever users to
          access this GPT without access to EverGPT.
        </p>

        <div class="ModalGptPublicLink__access">
          <UIIcon name="lockRegular" class="ModalGptPublicLink__access-icon" />
          <div class="ModalGptPublicLink__access-text">
            <span class="ModalGptPublicLink__access-title">
              Access · Foundever users only
            </span>
            <span class="ModalGptPublicLink__access-help">
              Anyone with the link and a Foundever account can access this GPT.
            </span>
          </div>
        </div>

        <p class="ModalGptPublicLink__note">
          Conversations aren’t saved and are lost when users refresh or close
          the page. Enabled capabilities will also be available in the web
          version of this GPT.
        </p>
      </div>

      <!-- Published (success + manage) -->
      <div
        v-else
        class="ModalGptPublicLink__body"
        data-testid="gpt-public-link-published-view"
      >
        <p class="ModalGptPublicLink__intro">
          Your GPT is now available through a shareable web link.
        </p>

        <UIShareableLinkPreview
          :url="shareUrl"
          heading="Web link"
          aria-label="Public GPT web link"
        />

        <p class="ModalGptPublicLink__note">
          Anyone with the link and a Foundever account can access this GPT.
        </p>

        <div class="ModalGptPublicLink__embed">
          <div class="ModalGptPublicLink__embed-block">
            <div class="ModalGptPublicLink__embed-head">
              <span class="ModalGptPublicLink__embed-title">
                Floating chat widget
              </span>
              <UIButton
                type="button"
                theme="secondary"
                size="sm"
                class="ModalGptPublicLink__copy-btn"
                aria-label="Copy code for floating chat widget"
                data-testid="gpt-public-link-widget-copy"
                @click="copySnippet('widget', widgetSnippet)"
              >
                <template v-if="copied.widget" #icon-before>
                  <UICheckIcon
                    class="ModalGptPublicLink__copy-icon"
                    name="check"
                  />
                </template>
                {{ copied.widget ? 'Copied' : 'Copy code' }}
              </UIButton>
            </div>
            <pre
              class="ModalGptPublicLink__embed-code"
              data-testid="gpt-public-link-widget-snippet"
              v-text="widgetSnippet"
            />
            <p class="ModalGptPublicLink__note">
              Paste on any internal Foundever page to add a floating chat
              button.
            </p>
          </div>

          <div class="ModalGptPublicLink__embed-block">
            <div class="ModalGptPublicLink__embed-head">
              <span class="ModalGptPublicLink__embed-title">
                Inline frame
              </span>
              <UIButton
                type="button"
                theme="secondary"
                size="sm"
                class="ModalGptPublicLink__copy-btn"
                aria-label="Copy code for inline frame"
                data-testid="gpt-public-link-iframe-copy"
                @click="copySnippet('iframe', iframeSnippet)"
              >
                <template v-if="copied.iframe" #icon-before>
                  <UICheckIcon
                    class="ModalGptPublicLink__copy-icon"
                    name="check"
                  />
                </template>
                {{ copied.iframe ? 'Copied' : 'Copy code' }}
              </UIButton>
            </div>
            <pre
              class="ModalGptPublicLink__embed-code"
              data-testid="gpt-public-link-iframe-snippet"
              v-text="iframeSnippet"
            />
            <p class="ModalGptPublicLink__note">
              Embeds the chat directly in a page as a fixed-size frame.
            </p>
          </div>
        </div>
      </div>
    </template>

    <template #actions>
      <!-- Publish confirmation -->
      <template v-if="confirmingPublish">
        <UIButton
          type="button"
          theme="secondary"
          @click="confirmingPublish = false"
        >
          Cancel
        </UIButton>
        <UIButton
          type="button"
          :isLoading="isPublishing"
          data-testid="gpt-public-link-confirm"
          @click="onPublish"
        >
          Publish
        </UIButton>
      </template>

      <!-- Unpublish confirmation -->
      <template v-else-if="confirmingUnpublish">
        <UIButton
          type="button"
          theme="secondary"
          @click="confirmingUnpublish = false"
        >
          Cancel
        </UIButton>
        <UIButton
          type="button"
          :isLoading="setActive.isPending.value"
          data-testid="gpt-public-link-unpublish-confirm"
          @click="onUnpublish"
        >
          Unpublish
        </UIButton>
      </template>

      <!-- Publish -->
      <template v-else-if="step === 'publish'">
        <UIButton type="button" theme="secondary" @click="emit('close')">
          Cancel
        </UIButton>
        <UIButton
          type="button"
          data-testid="gpt-public-link-publish"
          @click="confirmingPublish = true"
        >
          Publish to web
        </UIButton>
      </template>

      <!-- Published -->
      <template v-else-if="step === 'published'">
        <template v-if="justPublished">
          <UIButton
            type="button"
            data-testid="gpt-public-link-done"
            @click="emit('close')"
          >
            Done
          </UIButton>
        </template>
        <template v-else>
          <UIButton
            type="button"
            theme="tertiary"
            class="ModalGptPublicLink__unpublish-link"
            data-testid="gpt-public-link-unpublish"
            @click="confirmingUnpublish = true"
          >
            Unpublish
          </UIButton>
          <UIButton
            type="button"
            data-testid="gpt-public-link-done"
            @click="emit('close')"
          >
            Done
          </UIButton>
        </template>
      </template>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'

import { UIButton, UIIcon, UIModal, UISpinner } from '@foundever/ui-lib'

import { handleApiErrorToast } from '@/apiClient'
import { useAdminQueries } from '@/apiClient/queries/useAdminQueries'
import { useGptPublicLinkQueries } from '@/apiClient/queries/useGptPublicLinkQueries'
import type { IGptListItem } from '@/apiClient/types/gpt'
import type { IGptPublicLink } from '@/apiClient/types/publicGpt'
import { useToast } from '@/store/toast'
import {
  publicGptEmbedSnippet,
  publicGptShareHref,
  publicGptWidgetSnippet,
} from '@/utils/publicGpt'
// Local UIIcon (aliased) is kept only for the `check` glyph, which ui-lib does not
// ship yet; it is rendered in the ui-lib UIButton `icon-before` slot.
import UICheckIcon from '@/views/components/ui/icons/UIIcon.vue'
import UIShareableLinkPreview from '@/views/components/ui/misc/UIShareableLinkPreview.vue'

import type { AxiosError } from 'axios'

const props = defineProps<{
  isOpen?: boolean
  gpt: IGptListItem
}>()
const emit = defineEmits(['close'])

const { status, create, setActive } = useGptPublicLinkQueries()
const { updateGpt } = useAdminQueries()

// Only fetch the link status while the modal is open (avoids a request per GPT card).
const statusGptId = computed(() => (props.isOpen ? props.gpt.id : undefined))
const linkQuery = status(statusGptId)

// Link returned by generate — used for instant display before the status refetches.
const createdLink = ref<IGptPublicLink | null>(null)
const justPublished = ref(false)
const confirmingPublish = ref(false)
const confirmingUnpublish = ref(false)

// A public link can only exist for a public GPT, so publishing makes it public
// first (unless it already is).
const willBecomePublic = computed(() => !props.gpt.is_public)

const link = computed(() => linkQuery.data.value ?? createdLink.value ?? null)

const isInitialLoading = computed(
  () => props.isOpen && linkQuery.isLoading.value && !createdLink.value,
)

const step = computed<'loading' | 'publish' | 'published'>(() => {
  if (justPublished.value) return 'published'
  if (isInitialLoading.value) return 'loading'
  return link.value?.is_active ? 'published' : 'publish'
})

const title = computed(() => {
  if (confirmingPublish.value) return 'Publish this GPT?'
  if (confirmingUnpublish.value) return 'Unpublish GPT from web?'
  if (step.value === 'publish') return 'Publish GPT to web?'
  if (justPublished.value) return 'GPT published to web'
  return 'Web access'
})

const ownerName = computed(() => props.gpt.owner_name || 'You')

const isPublishing = computed(
  () =>
    updateGpt.isPending.value ||
    create.isPending.value ||
    setActive.isPending.value,
)

const shareUrl = computed(() =>
  link.value ? publicGptShareHref(link.value.share_token) : '',
)

const widgetSnippet = computed(() =>
  link.value ? publicGptWidgetSnippet(link.value.share_token) : '',
)
const iframeSnippet = computed(() =>
  link.value ? publicGptEmbedSnippet(link.value.share_token) : '',
)

// Independent per-button state so each button shows its own "Copied" confirmation
// (clicking one never resets another).
type SnippetKey = 'widget' | 'iframe'
const copied = ref<Record<SnippetKey, boolean>>({
  widget: false,
  iframe: false,
})
const copyResetTimers: Partial<
  Record<SnippetKey, ReturnType<typeof setTimeout>>
> = {}
const copySnippet = async (key: SnippetKey, text: string) => {
  try {
    await navigator.clipboard.writeText(text)
    copied.value[key] = true
    clearTimeout(copyResetTimers[key])
    copyResetTimers[key] = setTimeout(() => (copied.value[key] = false), 2000)
    // Announce via toast (aria-live) so the result reaches assistive tech, not just the
    // visual button label — mirrors UIShareableLinkPreview.onCopy.
    useToast().onSuccess('Code copied')
  } catch {
    // Clipboard blocked (e.g. insecure context) — leave the code visible to copy manually.
    useToast().onError('Could not copy code')
  }
}
onBeforeUnmount(() => {
  clearTimeout(copyResetTimers.widget)
  clearTimeout(copyResetTimers.iframe)
})

const onPublished = (data: IGptPublicLink) => {
  createdLink.value = data
  confirmingPublish.value = false
  justPublished.value = true
}

const onPublish = async () => {
  // A public link can only exist for a public GPT, so make it public first.
  // Always through the admin endpoint: this modal (like the link endpoints) is
  // admin-only, and the owner-scoped `PUT /gpts/{id}` responds 404 for any GPT
  // the admin holds no write permission on — most of Admin → GPT management.
  if (willBecomePublic.value) {
    try {
      await updateGpt.mutateAsync({ ...props.gpt, is_public: true })
    } catch (error) {
      handleApiErrorToast(error as AxiosError)
      return
    }
  }

  // Re-activating a previously-deactivated link vs creating a brand-new one.
  if (link.value) {
    setActive.mutate(
      { gptId: props.gpt.id, isActive: true },
      { onSuccess: onPublished },
    )
  } else {
    create.mutate(props.gpt.id, { onSuccess: onPublished })
  }
}

const onUnpublish = () =>
  // Close only on success — the mutation surfaces failures via its onError toast,
  // so a failed deactivation keeps the confirm view open to retry rather than
  // closing the modal while the link is still active.
  setActive.mutate(
    { gptId: props.gpt.id, isActive: false },
    { onSuccess: () => emit('close') },
  )

// Reset the transient state each time the modal is (re)opened.
watch(
  () => props.isOpen,
  (open) => {
    if (open) {
      createdLink.value = null
      justPublished.value = false
      confirmingPublish.value = false
      confirmingUnpublish.value = false
      copied.value = { widget: false, iframe: false }
    }
  },
)
</script>

<style lang="scss">
.ModalGptPublicLink.UIModalContainer {
  --modal-size: 32rem;

  .ModalGptPublicLink__body {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  .ModalGptPublicLink__loading {
    display: flex;
    justify-content: center;
    padding: 1.5rem 0;
  }

  .ModalGptPublicLink__intro {
    @include font-size-14px;
    margin: 0;
    color: var(--text-secondary);
  }

  .ModalGptPublicLink__access {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--border-heavy);
    border-radius: 0.5rem;
    background: var(--bg-elevated-secondary);
  }

  .ModalGptPublicLink__access-icon {
    --size: 1.25rem;
    --color: var(--icon-primary);
    flex-shrink: 0;
    margin-top: 0.125rem;
  }

  .ModalGptPublicLink__access-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .ModalGptPublicLink__access-title {
    @include font-size-14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .ModalGptPublicLink__access-help {
    @include font-size-12px;
    color: var(--text-tertiary);
  }

  .ModalGptPublicLink__note {
    @include font-size-12px;
    margin: 0;
    color: var(--text-tertiary);
  }

  .ModalGptPublicLink__embed {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 1rem;
    border-top: 1px solid var(--border-heavy);
  }

  .ModalGptPublicLink__embed-block {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .ModalGptPublicLink__embed-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
  }

  .ModalGptPublicLink__copy-btn {
    flex-shrink: 0;
  }

  .ModalGptPublicLink__copy-icon {
    --size: 0.875rem;
    --color: var(--icon-primary);
  }

  .ModalGptPublicLink__embed-title {
    @include font-size-14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .ModalGptPublicLink__embed-code {
    @include font-size-12px;
    margin: 0;
    padding: 0.75rem;
    border: 1px solid var(--border-heavy);
    border-radius: 0.5rem;
    background: var(--bg-elevated-secondary);
    color: var(--text-primary);
    font-family: monospace;
    white-space: pre-wrap;
    word-break: break-all;
    overflow-x: auto;
  }

  .ModalGptPublicLink__gpt-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    border: 1px solid var(--border-heavy);
    border-radius: 0.5rem;
  }

  .ModalGptPublicLink__gpt-icon {
    width: 2rem;
    height: 2rem;
    flex-shrink: 0;
    border-radius: 50%;
  }

  .ModalGptPublicLink__gpt-text {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    min-width: 0;
  }

  .ModalGptPublicLink__gpt-name {
    @include font-size-14px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .ModalGptPublicLink__gpt-meta {
    @include font-size-12px;
    color: var(--text-tertiary);
  }

  .ModalGptPublicLink__unpublish-link {
    margin-right: auto;
    text-decoration: underline;
  }
}
</style>
